import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { FairUseTier } from "@/api/admin/fair-use"

import {
  type AccessPolicyUpdate,
  clearModelAccessPolicy,
  createModel,
  type CreateModelRequest,
  deleteModel,
  type EffortAccessMap,
  getBudgetModel,
  getEmbeddingModels,
  getModelAccessPolicy,
  getModelBaseAccessPolicy,
  getModelBaseEffortAccess,
  getModelEffortAccess,
  getModelPlans,
  getSpecializedModels,
  updateModelAccessPolicy,
  updateModelBaseAccessPolicy,
  updateModelBaseEffortAccess,
  updateModelEffortAccess,
  updateModelPlans,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

/** Cache key of the chat-model list — the default list a status toggle patches. */
const MODELS_QUERY_KEY = ["admin", "models"] as const

export type UpdateModelStatusVariables = {
  modelId: string
  enabled: boolean
  /**
   * List to patch optimistically. Defaults to the chat models; the Embeddings
   * and Specialized tabs pass `["admin","embeddings"]` / `["admin","specialized"]`.
   */
  queryKey?: readonly unknown[]
}

// Model Management
export function useAdminModelsQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "models"],
    queryFn: async () => {
      const response = await api.get("/admin/models")
      const data = response.data || response
      // Ensure we always return an array
      return Array.isArray(data) ? data : []
    },
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

/**
 * Budget model mapping (HERMES Fas 6): the id hosted agents downgrade to on
 * `on_exceeded: "budget_model"`. Empty string = not configured — the wizard's
 * Budget tab warns when that combination is selected.
 */
export function useAdminBudgetModelQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "models", "budget-model"],
    queryFn: getBudgetModel,
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useCreateModelMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (config: CreateModelRequest) => {
      return await createModel(config)
    },
    onSuccess: () => {
      // Invalidate all model-related queries to refresh the list
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "embeddings"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "specialized"] })
    },
  })
}

export function useDeleteModelMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (modelId: string) => {
      return await deleteModel(modelId)
    },
    onSuccess: () => {
      // Invalidate all model-related queries to refresh the list
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "embeddings"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "specialized"] })
    },
  })
}

export function useAdminEmbeddingModelsQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "embeddings"],
    queryFn: getEmbeddingModels,
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useAdminSpecializedModelsQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "specialized"],
    queryFn: getSpecializedModels,
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useUpdateModelStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, enabled }: UpdateModelStatusVariables) => {
      const response = await api.put(`/admin/models/${modelId}/status`, { enabled })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update model status")
      }
      return data
    },
    onMutate: async ({ modelId, enabled, queryKey }) => {
      // The same endpoint serves chat, embedding and specialized rows, but each
      // list is cached under its own key — patch the one the row came from.
      const listKey = queryKey ?? MODELS_QUERY_KEY

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listKey })

      // Snapshot the previous value
      const previousModels =
        queryClient.getQueryData<Array<{ id: string; enabled: boolean }>>(listKey)

      // Optimistically update to the new value
      if (previousModels) {
        queryClient.setQueryData(
          listKey,
          previousModels.map((model) => (model.id === modelId ? { ...model, enabled } : model))
        )
      }

      // Return a context object with the snapshotted value
      return { previousModels, listKey }
    },
    onError: (err, variables, context) => {
      const listKey = context?.listKey ?? variables.queryKey ?? MODELS_QUERY_KEY
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousModels) {
        queryClient.setQueryData(listKey, context.previousModels)
      }
      // Refetch on error to ensure consistency
      void queryClient.invalidateQueries({ queryKey: listKey })
    },
    // Don't refetch on success - we already updated optimistically
  })
}

export function useAdminModelAccessPolicyQuery(modelId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "models", modelId, "access"],
    queryFn: () => getModelAccessPolicy(modelId ?? ""),
    enabled: enabled && !!modelId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateModelAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, policy }: { modelId: string; policy: AccessPolicyUpdate }) => {
      return await updateModelAccessPolicy(modelId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "models", variables.modelId, "access"],
      })
    },
  })
}

export function useAdminModelBaseAccessPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "models", "base", "access"],
    queryFn: () => getModelBaseAccessPolicy(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateModelBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ policy }: { policy: AccessPolicyUpdate }) => {
      return await updateModelBaseAccessPolicy(policy)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models", "base", "access"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
    },
  })
}

export function useAdminModelBaseEffortAccessQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "models", "base", "effort-access"],
    queryFn: () => getModelBaseEffortAccess(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateModelBaseEffortAccessMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ map }: { map: EffortAccessMap }) => {
      return await updateModelBaseEffortAccess(map)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models", "base", "effort-access"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
    },
  })
}

export function useAdminModelEffortAccessQuery(modelId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "models", modelId, "effort-access"],
    queryFn: () => getModelEffortAccess(modelId ?? ""),
    enabled: enabled && !!modelId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateModelEffortAccessMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, map }: { modelId: string; map: EffortAccessMap }) => {
      return await updateModelEffortAccess(modelId, map)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "models", variables.modelId, "effort-access"],
      })
    },
  })
}

export function useClearModelAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId }: { modelId: string }) => {
      return await clearModelAccessPolicy(modelId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "models", variables.modelId, "access"],
      })
    },
  })
}

// =============================================================================
// Fair-use plan restriction (#3087, spec R2)
// =============================================================================

/**
 * A model's plan allowlist, read fresh whenever the edit dialog opens.
 *
 * `staleTime: 0` on purpose: this is a whole-list PUT with no optimistic
 * concurrency, so a cached list from an earlier session would let one admin
 * silently overwrite another's restriction with what their browser remembered.
 */
export function useAdminModelPlansQuery(modelId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "models", modelId, "plans"],
    queryFn: () => getModelPlans(modelId ?? ""),
    enabled: enabled && !!modelId,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  })
}

/**
 * Replace a model's plan allowlist.
 *
 * Invalidates the chat-model list as well as the per-model entry: `plans` is
 * carried on every `/api/admin/models` row, so the table's badge would keep
 * quoting the pre-save restriction.
 */
export function useUpdateModelPlansMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ modelId, plans }: { modelId: string; plans: FairUseTier[] | null }) => {
      return await updateModelPlans(modelId, plans)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "models"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "models", variables.modelId, "plans"],
      })
    },
  })
}
